import { useTautulliStatus } from '../tautulli';
import { useAddons, type Addon } from '../addons';
import { useEffect, useMemo, useState } from 'react';
import { ApiError, call, layoutApi, type HubDef, type RowFilter, type ShelfKind, type LayoutDef, type WidgetDef, type WidgetOptions, type WidgetSource, type WidgetView as View } from '../api';
import { useApp } from '../context';
import { HUB_PAGES, loadLayout, pagesOf } from '../layout';
import { linkProps } from '../router';
import { clearWidgetCache, WidgetView } from '../components/Widgets';
import { LeaguePicker } from '../components/LeaguePicker';
import { loadScLeagues, useScSettings, type ScLeague } from '../sportcenter';
import { NowTvPicker, SportFilter } from '../components/LivePickers';
import { RowLibrary } from '../components/RowLibrary';

/**
 * Startscherm aanpassen: hubs en widgets toevoegen, ordenen en instellen.
 * Opslaan voor iedereen (eigenaar) of alleen voor dit profiel.
 */

type Scope = 'default' | 'profile';

const VIEWS: { id: View; label: string }[] = [
  { id: 'spotlight', label: 'Spotlight' },
  { id: 'billboard', label: 'Billboard (volgt selectie)' },
  { id: 'poster', label: 'Poster' },
  { id: 'netflix', label: 'Netflix ✦' },
  { id: 'landscape', label: 'Landscape' },
  { id: 'wide', label: 'Breed + logo' },
  { id: 'top10', label: 'Top 10' },
  { id: 'wall', label: 'Wall' },
];

const PLEX_LISTS = [
  { id: 'continueWatching', label: 'Verder kijken' },
  { id: 'recentlyAdded', label: 'Recent toegevoegd' },
  { id: 'unwatched', label: 'Ongezien' },
  { id: 'genre', label: 'Genre' },
  { id: 'watchlist', label: 'Mijn watchlist (Plex)' },
  { id: 'collection', label: 'Collectie' },
  { id: 'playlist', label: 'Playlist' },
  { id: 'shelf', label: "Collector's Shelf (4K, Dolby Vision, Atmos…)" },
] as const;

const SHELF_NAMES: Record<ShelfKind, string> = {
  dv: 'Dolby Vision', '4k': '4K Ultra HD', hdr10p: 'HDR10+', atmos: 'Dolby Atmos', dtsx: 'DTS:X', remux: 'Remux',
};

const TMDB_LISTS = [
  { id: 'trending_day', label: 'Trending vandaag' },
  { id: 'trending_week', label: 'Trending deze week' },
  { id: 'popular', label: 'Populair' },
  { id: 'top_rated', label: 'Best beoordeeld' },
  { id: 'now_playing', label: 'Nu in de bioscoop (films)' },
  { id: 'upcoming', label: 'Binnenkort in de bioscoop (films)' },
  { id: 'on_the_air', label: 'Nu op tv (series)' },
  { id: 'provider', label: 'Op een streamingdienst (NL)' },
  { id: 'genre', label: 'Genre' },
] as const;

/** Bronnen die alleen via een geïmporteerde collectie of een logo-tegel binnenkomen */
const TMDB_EXTRA: Record<string, string> = {
  company: 'Studio', network: 'Netwerk', keyword: 'Trefwoord', person: 'Acteur', director: 'Regisseur',
  collection: 'Filmreeks', list: 'TMDB-lijst', discover: 'Eigen selectie',
};

const TRAKT_LISTS = [
  { id: 'trending', label: 'Trending' },
  { id: 'popular', label: 'Populair' },
  { id: 'anticipated', label: 'Meest verwacht' },
  { id: 'boxoffice', label: 'Box office (films)' },
  { id: 'watchlist', label: 'Mijn watchlist' },
  { id: 'recommendations', label: 'Aanbevolen voor mij' },
  { id: 'custom', label: 'Eigen lijst (link)' },
] as const;

function rid(): string {
  return Math.random().toString(36).slice(2, 10);
}

function describe(s: WidgetSource): string {
  switch (s.kind) {
    case 'plex': return `Plex · ${PLEX_LISTS.find((l) => l.id === s.list)?.label ?? s.list}${s.name ? ` · ${s.name}` : ''}`;
    case 'tmdb': return `TMDB · ${TMDB_LISTS.find((l) => l.id === s.list)?.label ?? TMDB_EXTRA[s.list] ?? s.list}${s.refName ? ` · ${s.refName}` : ''}${s.media === 'tv' ? ' · series' : s.media === 'movie' ? ' · films' : ''}${s.sort === 'new' ? ' · nieuwste' : ''}`;
    case 'mdblist': return `MDBList · ${s.name ?? s.list}`;
    case 'trakt': return s.list === 'custom' ? `Trakt · ${s.name ?? 'eigen lijst'}` : `Trakt · ${TRAKT_LISTS.find((l) => l.id === s.list)?.label ?? s.list}${s.media === 'tv' ? ' · series' : ' · films'}`;
    case 'simkl': return s.list === 'custom' ? `SIMKL · ${s.name ?? 'eigen lijst'}` : `SIMKL · ${s.list === 'plantowatch' ? 'Plan to watch' : s.list === 'watching' ? 'Bezig' : 'Trending'} · ${s.media === 'anime' ? 'anime' : s.media === 'tv' ? 'series' : 'films'}`;
    case 'live': return s.list === 'leagues' ? "Live TV · competitielogo's (klik = zenders)" : s.list === 'nowtv' ? 'Live TV · nu op tv' : 'Live TV · sport (NL bovenaan, jouw clubs eerst)';
    case 'seerr': return 'Seerr · mijn verzoeken';
    case 'calendar': return `Kalender · ${s.list === 'movies' ? 'films' : s.list === 'all' ? 'alles' : 'nieuwe afleveringen'}`;
    case 'personal': return `Voor jou · ${s.list === 'saga' ? 'verder in je reeks' : s.list === 'actor' ? 'met een acteur uit iets wat je keek' : s.list === 'director' ? 'meer van de regisseur' : 'omdat je … keek'}`;
    case 'brands': return `Logo's · ${s.list === 'studios' ? 'studio\'s' : s.list === 'categories' ? 'categorieën' : 'netwerken en diensten'}`;
    case 'addon': return `Addon · ${s.name ?? s.catalog}`;
    case 'merge': return `Samengevoegd · ${s.parts.map(describe).join(' + ')}`;
    case 'tautulli': return 'Tautulli · nu actief op de server (alleen beheerder)';
    case 'sportcenter': return `Sport · ${SC_LISTS.find((l) => l.id === s.list)?.label ?? s.list}`;
  }
}

let addonSnapshot: Addon[] = [];
function videoAddonsNow(): Addon[] {
  return addonSnapshot.filter((a) => a.kind === 'video');
}

function defaultSource(kind: WidgetSource['kind']): WidgetSource {
  switch (kind) {
    case 'plex': return { kind: 'plex', list: 'recentlyAdded' };
    case 'tmdb': return { kind: 'tmdb', list: 'trending_week', media: 'all' };
    case 'mdblist': return { kind: 'mdblist', list: '' };
    case 'trakt': return { kind: 'trakt', list: 'trending', media: 'movie' };
    case 'simkl': return { kind: 'simkl', list: 'trending', media: 'anime', period: 'week' };
    case 'live': return { kind: 'live', list: 'sport' };
    case 'seerr': return { kind: 'seerr', list: 'requests' };
    case 'calendar': return { kind: 'calendar', list: 'episodes' };
    case 'personal': return { kind: 'personal', list: 'because' };
    case 'brands': return { kind: 'brands', list: 'networks' };
    case 'tautulli': return { kind: 'tautulli', list: 'activity' };
    case 'merge': return { kind: 'tmdb', list: 'trending_week', media: 'all' };
    case 'sportcenter': return { kind: 'sportcenter', list: 'ticker' };
    case 'addon': {
      const a = videoAddonsNow().find((x) => x.widgets && x.catalogs.some((c) => !c.searchOnly));
      const c = a?.catalogs.find((x) => !x.searchOnly);
      return a && c ? { kind: 'addon', list: 'catalog', addonId: a.id, catalog: c.key, name: `${c.name} · ${a.name}` } : { kind: 'plex', list: 'recentlyAdded' };
    }
  }
}

export function Editor() {
  const { me, libraries } = useApp();
  const [canDefault, setCanDefault] = useState(false);
  const [presets, setPresets] = useState<{ id: string; name: string }[]>([]);
  const [importing, setImporting] = useState(false);
  const [library, setLibrary] = useState(false);
  const [scope, setScope] = useState<Scope>('profile');
  const [layout, setLayoutState] = useState<LayoutDef | null>(null);
  const [hasOwn, setHasOwn] = useState(false);
  const [hubId, setHubId] = useState<string | null>(null);
  const [widgetId, setWidgetId] = useState<string | null>(null);
  const [options, setOptions] = useState<WidgetOptions | null>(null);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    layoutApi.current().then((r) => {
      setCanDefault(r.canEditDefault);
      setPresets(r.presets);
      setScope(r.canEditDefault && r.scope === 'default' ? 'default' : 'profile');
    }).catch(() => setMsg('Indeling laden mislukt.'));
    layoutApi.options().then(setOptions).catch(() => setOptions(null));
  }, []);

  // Indeling voor de gekozen scope laden
  useEffect(() => {
    if (!presets.length) return;
    (async () => {
      if (scope === 'default') {
        const r = await layoutApi.get('default');
        setLayoutState(r.layout);
        setHasOwn(false);
      } else {
        const own = await layoutApi.get('profile');
        setHasOwn(!!own.layout);
        setLayoutState(own.layout ?? (await layoutApi.get('default')).layout);
      }
      setDirty(false);
    })().catch(() => setMsg('Indeling laden mislukt.'));
  }, [scope, presets.length]);

  useEffect(() => {
    if (layout && !layout.hubs.some((h) => h.id === hubId)) setHubId(layout.hubs[0]?.id ?? null);
  }, [layout, hubId]);

  const hub = layout?.hubs.find((h) => h.id === hubId) ?? null;
  const widget = hub?.widgets.find((w) => w.id === widgetId) ?? null;

  const update = (fn: (l: LayoutDef) => LayoutDef) => {
    setLayoutState((l) => (l ? fn(structuredClone(l)) : l));
    setDirty(true);
    setMsg(null);
  };
  const updateHub = (fn: (h: HubDef) => void) => update((l) => {
    const h = l.hubs.find((x) => x.id === hubId);
    if (h) fn(h);
    return l;
  });
  const updateWidget = (patch: Partial<WidgetDef>) => updateHub((h) => {
    const w = h.widgets.find((x) => x.id === widgetId);
    if (w) Object.assign(w, patch);
  });
  const move = <T,>(list: T[], i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
  };

  const save = async () => {
    if (!layout) return;
    setBusy(true);
    try {
      await layoutApi.save(scope, layout);
      if (scope === 'profile') setHasOwn(true);
      setDirty(false);
      clearWidgetCache();
      await loadLayout(true);
      setMsg(scope === 'default' ? 'Opgeslagen voor iedereen (behalve profielen met een eigen indeling).' : 'Opgeslagen voor dit profiel.');
    } catch (err) {
      setMsg(err instanceof ApiError && err.message !== err.code ? err.message : 'Opslaan mislukt.');
    } finally {
      setBusy(false);
    }
  };

  /** Kant-en-klare widgets en een Kids-hub. */
  const addTemplate = (kind: string) => {
    if (!kind) return;
    const w = (title: string, source: WidgetSource, view: WidgetDef['view'], extra: Partial<WidgetDef> = {}): WidgetDef =>
      ({ id: rid(), title, source, view, show: 'all', limit: view === 'top10' ? 10 : 20, enabled: true, ...extra });
    if (kind === 'kids') {
      const id = rid();
      const kidsLib = libraries.find((l) => /kid|kinder|jeugd|famil/i.test(l.title));
      const widgets: WidgetDef[] = [
        ...(kidsLib ? [w(`Nieuw in ${kidsLib.title}`, { kind: 'plex', list: 'recentlyAdded', library: kidsLib.key }, 'poster')] : []),
        ...(options?.available.tmdb ? [
          w('Familiefilms', { kind: 'tmdb', list: 'genre', media: 'movie', genreId: 10751 }, 'poster', { show: 'owned' }),
          w('Animatie', { kind: 'tmdb', list: 'genre', media: 'movie', genreId: 16 }, 'landscape', { show: 'owned' }),
          w('Kinderseries', { kind: 'tmdb', list: 'genre', media: 'tv', genreId: 10762 }, 'poster', { show: 'owned' }),
        ] : [w('Recent toegevoegd', { kind: 'plex', list: 'recentlyAdded' }, 'poster')]),
        w('Verder kijken', { kind: 'plex', list: 'continueWatching' }, 'landscape'),
      ];
      update((l) => { l.hubs.push({ id, name: 'Kids', widgets }); return l; });
      setHubId(id);
      setWidgetId(null);
      setMsg(`Kids-hub toegevoegd. Tip: open dit scherm als het kinderprofiel en sla op met "Voor: Alleen …", dan geldt hij alleen daar. Kinderprofielen zien sowieso alleen wat in hun bibliotheek staat.`);
      return;
    }
    if (kind === 'foryou' || kind === 'brands') {
      const list: WidgetDef[] = kind === 'foryou'
        ? [
          w('Omdat je … keek', { kind: 'personal', list: 'because' }, 'poster'),
          w('Met een acteur', { kind: 'personal', list: 'actor' }, 'poster'),
          w('Meer van de regisseur', { kind: 'personal', list: 'director' }, 'landscape'),
        ]
        : [
          w('Netwerken & diensten', { kind: 'brands', list: 'networks' }, 'poster'),
          w("Studio's", { kind: 'brands', list: 'studios' }, 'poster'),
          w('Categorieën', { kind: 'brands', list: 'categories' }, 'poster'),
        ];
      if (!hub) return;
      updateHub((h) => { h.widgets.push(...list); });
      setWidgetId(list[0].id);
      setMsg(kind === 'foryou' ? 'Drie persoonlijke rijen toegevoegd; de koppen passen zich aan op wat je keek.' : "Logo-rijen en categorieën toegevoegd.");
      return;
    }
    let widget: WidgetDef | null = null;
    if (kind === 'netflix') {
      widget = w('Nieuw op Netflix NL', { kind: 'tmdb', list: 'provider', media: 'movie', providerId: 8, sort: 'new' }, 'poster', { show: 'missing' });
    } else if (kind === 'calendar') {
      widget = w('Nieuwe afleveringen', { kind: 'calendar', list: 'episodes' }, 'landscape');
    } else if (kind === 'top10') {
      const top = options?.mdblistTop ?? [];
      const pick = top.find((l) => /netflix/i.test(l.name)) ?? top.find((l) => /imdb.*250|top 250/i.test(l.name)) ?? top[0];
      widget = w(pick ? `Top 10 · ${pick.name}` : 'Top 10', { kind: 'mdblist', list: pick?.id ?? '', name: pick?.name }, 'top10');
    }
    if (!widget || !hub) return;
    const add = widget;
    updateHub((h) => { h.widgets.push(add); });
    setWidgetId(add.id);
    setMsg(kind === 'top10' ? 'Top 10 toegevoegd. Kies rechts een andere lijst als je wilt (bijv. IMDb Top 250).' : kind === 'netflix' ? 'Toegevoegd: de nieuwste films op Netflix NL die je nog niet hebt. Rechts kun je naar series wisselen.' : 'Toegevoegd.');
  };

  const applyPreset = async (id: string) => {
    if (!id) return;
    const r = await layoutApi.preset(id).catch(() => null);
    if (!r) return;
    setLayoutState(r.layout);
    setHubId(r.layout.hubs[0]?.id ?? null);
    setWidgetId(null);
    setDirty(true);
    setMsg('Startpunt geladen. Pas aan en klik op Opslaan.');
  };

  if (!layout) return <main className="page"><header className="page-head"><h1>Startscherm aanpassen</h1></header>{msg && <p className="placeholder-row error">{msg}</p>}</main>;

  return (
    <main className="page editor">
      <header className="page-head editor-head">
        <h1>Startscherm aanpassen</h1>
        <div className="spacer" />
        <label className="ed-field-inline">
          Voor
          <select value={scope} onChange={(e) => setScope(e.target.value as Scope)} disabled={dirty}>
            <option value="profile">Alleen {me.profile?.title ?? 'dit profiel'}</option>
            {canDefault && <option value="default">Iedereen</option>}
          </select>
        </label>
        <label className="ed-field-inline">
          Startpunt
          <select value="" onChange={(e) => void applyPreset(e.target.value)}>
            <option value="">Kies…</option>
            {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        {scope === 'profile' && hasOwn && (
          <button className="btn btn-ghost" onClick={async () => { await layoutApi.resetProfile().catch(() => undefined); setHasOwn(false); clearWidgetCache(); await loadLayout(true); setScope('profile'); setLayoutState((await layoutApi.get('default')).layout); setMsg('Eigen indeling verwijderd: dit profiel volgt weer de indeling voor iedereen.'); }}>
            Eigen indeling verwijderen
          </button>
        )}
        {canDefault && (
          <button className="btn btn-ghost" title="Nieuwe gebruikers (via een uitnodiging) die Standaard kiezen of de stap overslaan, krijgen deze indeling" onClick={async () => {
            try {
              await call('/api/layout/starter', { method: 'PUT', body: JSON.stringify(layout) });
              setMsg('Opgeslagen als startscherm voor nieuwe gebruikers ("Standaard" in de welkomst-wizard). Bestaande gebruikers houden hun eigen indeling.');
            } catch {
              setMsg('Opslaan als standaard mislukt.');
            }
          }}>Standaard voor nieuwe gebruikers</button>
        )}
        <button className="btn btn-ghost" onClick={() => setImporting(true)}>Collecties importeren</button>
        <a className="btn" {...linkProps('/')}>Bekijken</a>
        <button className="btn btn-primary" onClick={() => void save()} disabled={!dirty || busy}>{busy ? 'Opslaan…' : dirty ? 'Opslaan' : 'Opgeslagen'}</button>
      </header>
      {importing && (
        <ImportCollections
          room={12 - layout.hubs.length}
          onClose={() => setImporting(false)}
          onImport={(hubs, note) => {
            update((l) => { l.hubs.push(...hubs.slice(0, Math.max(0, 12 - l.hubs.length))); return l; });
            if (hubs[0]) setHubId(hubs[0].id);
            setWidgetId(null);
            setImporting(false);
            setMsg(note);
          }}
        />
      )}
      {dirty && <p className="ed-hint subtle">Wissel van "Voor" pas na opslaan.</p>}
      {msg && <p className="placeholder-row" role="status">{msg}</p>}

      <div className="ed-cols">
        {/* Hubs */}
        <aside className="ed-hubs" aria-label="Hubs">
          <div className="ed-label">Hubs</div>
          {layout.hubs.map((h, i) => (
            <div key={h.id} className={`ed-hub ${h.id === hubId ? 'is-active' : ''}`}>
              <button className="ed-hub-name" onClick={() => { setHubId(h.id); setWidgetId(null); }} aria-pressed={h.id === hubId}>
                {h.name} <span className="subtle">{h.widgets.length}</span>
              </button>
              <span className="ed-row-tools">
                <button className="icon-mini" aria-label={`${h.name} omhoog`} disabled={i === 0} onClick={() => update((l) => { move(l.hubs, i, -1); return l; })}>▲</button>
                <button className="icon-mini" aria-label={`${h.name} omlaag`} disabled={i === layout.hubs.length - 1} onClick={() => update((l) => { move(l.hubs, i, 1); return l; })}>▼</button>
              </span>
            </div>
          ))}
          <button className="ed-add" onClick={() => {
            const id = rid();
            update((l) => { l.hubs.push({ id, name: 'Nieuwe hub', widgets: [] }); return l; });
            setHubId(id);
            setWidgetId(null);
          }}>+ Nieuwe hub</button>
          {hub && (
            <div className="ed-hub-edit">
              <label className="ed-field">Naam
                <input className="text-input" value={hub.name} maxLength={30} onChange={(e) => updateHub((h) => { h.name = e.target.value; })} />
              </label>
              {layout.hubs[0]?.id !== hub.id && (
                <HubPagesField hub={hub} onChange={(pages, showOnHome) => updateHub((h) => { h.pages = pages; h.library = undefined; h.showOnHome = showOnHome ? undefined : false; })} />
              )}
              <HubCoverField hub={hub} onChange={(cover) => updateHub((h) => { h.cover = cover; })} />
              {layout.hubs.length > 1 && (
                <button className="btn btn-ghost btn-sm" onClick={() => update((l) => { l.hubs = l.hubs.filter((x) => x.id !== hubId); return l; })}>Hub verwijderen</button>
              )}
              {layout.hubs[0]?.id === hub.id && <span className="subtle hint">De eerste hub is je Home.</span>}
            </div>
          )}
        </aside>

        {/* Widgets */}
        <section className="ed-widgets" aria-label="Widgets">
          <div className="ed-label">Widgets van {hub?.name}</div>
          {hub?.widgets.map((w, i) => (
            <div key={w.id} className={`ed-widget ${w.id === widgetId ? 'is-active' : ''} ${w.enabled ? '' : 'is-off'}`}>
              <span className="ed-row-tools">
                <button className="icon-mini" aria-label={`${w.title} omhoog`} disabled={i === 0} onClick={() => updateHub((h) => move(h.widgets, i, -1))}>▲</button>
                <button className="icon-mini" aria-label={`${w.title} omlaag`} disabled={i === hub.widgets.length - 1} onClick={() => updateHub((h) => move(h.widgets, i, 1))}>▼</button>
              </span>
              <span className="ed-view-badge">{VIEWS.find((v) => v.id === w.view)?.label}</span>
              <button className="ed-widget-main" onClick={() => setWidgetId(w.id)} aria-pressed={w.id === widgetId}>
                <strong>{w.title}</strong>
                <span className="subtle">{describe(w.source)}{w.show !== 'all' ? ` · ${w.show === 'owned' ? 'alleen in bibliotheek' : 'alleen aanvragen'}` : ''}</span>
              </button>
              <label className="toggle ed-toggle">
                <input type="checkbox" checked={w.enabled} onChange={() => updateHub((h) => { const x = h.widgets[i]; x.enabled = !x.enabled; })} />
                <span className="visually-hidden">Zichtbaar</span>
              </label>
              <button className="icon-mini" aria-label={`${w.title} verwijderen`} onClick={() => { updateHub((h) => { h.widgets.splice(i, 1); }); if (widgetId === w.id) setWidgetId(null); }}>✕</button>
            </div>
          ))}
          {hub && (
            <label className="ed-field ed-templates">Sjabloon toevoegen
              <select value="" onChange={(e) => addTemplate(e.target.value)}>
                <option value="">Kies…</option>
                <option value="netflix" disabled={!options?.available.tmdb}>Nieuw op Netflix NL · aanvragen</option>
                <option value="top10" disabled={!options?.available.mdblist}>Top 10 uit een MDBList-lijst</option>
                <option value="calendar" disabled={!options?.available.tmdb}>Nieuwe afleveringen (kalender)</option>
                <option value="foryou" disabled={!options?.available.tmdb}>Voor jou: omdat je … keek, acteur, regisseur</option>
                <option value="brands" disabled={!options?.available.tmdb}>Netwerken, studio's en categorieën</option>
                <option value="kids">Kids-hub (nieuwe hub)</option>
              </select>
            </label>
          )}
          {hub && (
            <button className="ed-add" onClick={() => {
              const id = rid();
              const src: WidgetSource = options?.available.tmdb ? { kind: 'tmdb', list: 'trending_week', media: 'all' } : { kind: 'plex', list: 'recentlyAdded' };
              updateHub((h) => { h.widgets.push({ id, title: options?.available.tmdb ? 'Trending deze week' : 'Recent toegevoegd', source: src, view: 'poster', show: 'all', limit: 20, enabled: true }); });
              setWidgetId(id);
            }}>+ Widget toevoegen</button>
          )}
          {hub && !library && (
            <button className="ed-add" disabled={!options?.available.tmdb} title={options?.available.tmdb ? undefined : 'Hiervoor is een TMDB-sleutel nodig'} onClick={() => setLibrary(true)}>+ Uit de rijenbibliotheek</button>
          )}
          {hub && library && (
            <RowLibrary
              existing={hub.widgets}
              room={30 - hub.widgets.length}
              onClose={() => setLibrary(false)}
              onAdd={(rows, note) => {
                const list: WidgetDef[] = rows.map((r) => ({ id: rid(), title: r.title, source: r.source, view: r.view ?? 'poster', show: 'all', limit: 20, enabled: true }));
                if (list.length) {
                  updateHub((h) => { h.widgets.push(...list); });
                  setWidgetId(list[list.length - 1].id);
                }
                setMsg(note);
              }}
            />
          )}
        </section>

        {/* Instellingen */}
        <aside className="ed-settings" aria-label="Widget-instellingen">
          {widget ? <WidgetSettings key={widget.id} w={widget} siblings={hub?.widgets.filter((x) => x.id !== widget.id) ?? []} options={options} onChange={updateWidget} /> : <p className="subtle">Kies een widget om die in te stellen.</p>}
        </aside>
      </div>
    </main>
  );
}

/** Waar staat deze hub: als knop op Home en/of bovenaan een of meer bibliotheken en menu-pagina's. */
function HubPagesField({ hub, onChange }: { hub: HubDef; onChange: (pages: string[], showOnHome: boolean) => void }) {
  const { libraries } = useApp();
  const video = libraries.filter((l) => l.type === 'movie' || l.type === 'show');
  const auto = hub.id === 'films' ? video.find((l) => l.type === 'movie') : hub.id === 'series' ? video.find((l) => l.type === 'show') : undefined;
  const pages = pagesOf(hub) ?? (auto ? [auto.key] : []);
  const onHome = hub.showOnHome !== false;
  const toggle = (id: string) => onChange(pages.includes(id) ? pages.filter((x) => x !== id) : [...pages, id], onHome);
  const opts = [...video.map((l) => ({ id: l.key, label: l.title })), ...HUB_PAGES];
  return (
    <div className="ed-field">
      <span>Waar tonen</span>
      <div className="chip-wrap">
        <button className="chip" aria-pressed={onHome} onClick={() => onChange(pages, !onHome)}>Knop op Home</button>
      </div>
      <span className="subtle">Bovenaan deze menu-pagina's</span>
      <div className="chip-wrap">
        {opts.map((o) => (
          <button key={o.id} className="chip" aria-pressed={pages.includes(o.id)} onClick={() => toggle(o.id)}>{o.label}</button>
        ))}
      </div>
      <span className="subtle hint">Kies zoveel pagina's als je wilt: dezelfde widgets staan dan bovenaan elk ervan. Zet "Knop op Home" uit als de hub alleen op die pagina's hoort.</span>
    </div>
  );
}

function WidgetSettings({ w, siblings, options, onChange }: { w: WidgetDef; siblings: WidgetDef[]; options: WidgetOptions | null; onChange: (p: Partial<WidgetDef>) => void }) {
  const { libraries, plex } = useApp();
  const videoLibs = libraries.filter((l) => l.type === 'movie' || l.type === 'show');
  const s = w.source;
  const setSource = (src: WidgetSource) => onChange({ source: src });
  const addonState = useAddons();
  const tautulli = useTautulliStatus(useApp().me.profile?.uuid ?? '');
  const videoAddons = (addonState?.addons ?? []).filter((a) => a.kind === 'video');
  addonSnapshot = addonState?.addons ?? [];
  const [plexGenres, setPlexGenres] = useState<{ key: string; title: string }[]>([]);
  const [mdbUrl, setMdbUrl] = useState(s.kind === 'mdblist' ? s.list : '');
  const external = s.kind === 'tmdb' || s.kind === 'mdblist' || s.kind === 'trakt' || s.kind === 'simkl' || s.kind === 'merge' || (s.kind === 'plex' && s.list === 'watchlist');
  const [collections, setCollections] = useState<{ ratingKey: string; title: string; childCount?: number }[]>([]);
  const [playlists, setPlaylists] = useState<{ ratingKey: string; title: string; leafCount?: number }[]>([]);

  const plexLib = s.kind === 'plex' ? s.library ?? videoLibs[0]?.key : undefined;
  useEffect(() => {
    if (s.kind !== 'plex' || s.list !== 'genre' || !plexLib) return;
    plex.genres(plexLib).then(setPlexGenres).catch(() => setPlexGenres([]));
  }, [plex, plexLib, s.kind, s.kind === 'plex' ? s.list : '']);
  useEffect(() => {
    if (s.kind !== 'plex' || s.list !== 'collection' || !plexLib) return;
    plex.collections(plexLib).then(setCollections).catch(() => setCollections([]));
  }, [plex, plexLib, s.kind, s.kind === 'plex' ? s.list : '']);
  useEffect(() => {
    if (s.kind !== 'plex' || s.list !== 'playlist') return;
    plex.playlists().then(setPlaylists).catch(() => setPlaylists([]));
  }, [plex, s.kind, s.kind === 'plex' ? s.list : '']);

  // Voorbeeld pas verversen als je even niets wijzigt
  const [previewW, setPreviewW] = useState(w);
  useEffect(() => {
    const t = window.setTimeout(() => setPreviewW(w), 600);
    return () => window.clearTimeout(t);
  }, [w]);
  const previewKey = useMemo(() => JSON.stringify(previewW), [previewW]);

  const avail = options?.available;
  const kinds: { id: WidgetSource['kind']; label: string; ok: boolean; why?: string }[] = [
    { id: 'plex', label: 'Plex', ok: true },
    { id: 'tmdb', label: 'TMDB', ok: !!avail?.tmdb, why: 'TMDB-sleutel nodig' },
    { id: 'mdblist', label: 'MDBList', ok: !!avail?.mdblist, why: 'MDBList-sleutel nodig' },
    { id: 'trakt', label: 'Trakt', ok: !!avail?.trakt, why: 'Trakt nog niet ingesteld' },
    { id: 'simkl', label: 'SIMKL', ok: !!avail?.simkl, why: 'SIMKL nog niet ingesteld' },
    { id: 'live', label: 'Live TV', ok: true },
    { id: 'seerr', label: 'Seerr', ok: true },
    { id: 'calendar', label: 'Kalender', ok: !!avail?.tmdb, why: 'TMDB-sleutel nodig' },
    { id: 'personal', label: 'Voor jou', ok: !!avail?.tmdb, why: 'TMDB-sleutel nodig' },
    { id: 'brands', label: "Logo's & categorieën", ok: !!avail?.tmdb, why: 'TMDB-sleutel nodig' },
    { id: 'sportcenter', label: 'Sport', ok: true },
    ...(tautulli?.widget ? [{ id: 'tautulli' as const, label: 'Tautulli', ok: true }] : []),
    ...((addonState?.manage ?? addonState?.allowed) ? [{ id: 'addon' as const, label: 'Addons', ok: videoAddons.some((a) => a.widgets && a.catalogs.length), why: 'Voeg eerst een video-addon met catalogi toe' }] : []),
  ];

  return (
    <div className="ed-form">
      <label className="ed-field">Titel
        <input className="text-input" value={w.title} maxLength={80} onChange={(e) => onChange({ title: e.target.value })} />
      </label>

      <div className="ed-field">
        <span>Bron</span>
        <div className="chip-wrap" role="radiogroup" aria-label="Bron">
          {kinds.map((k) => (
            <button key={k.id} role="radio" aria-checked={s.kind === k.id} className="chip" disabled={!k.ok} title={k.ok ? undefined : k.why} aria-pressed={s.kind === k.id} onClick={() => setSource(defaultSource(k.id))}>
              {k.label}
            </button>
          ))}
        </div>
      </div>

      <MergeFields s={s} siblings={siblings} onChange={(src) => onChange({ source: src })} />

      {s.kind === 'sportcenter' && <SportSourceFields s={s} onChange={(src, title) => onChange(title ? { source: src, title } : { source: src })} />}

      {s.kind === 'addon' && (
        <label className="ed-field">Catalogus
          <select value={`${s.addonId}|${s.catalog}`} onChange={(e) => {
            const [addonId, ...rest] = e.target.value.split('|');
            const catalog = rest.join('|');
            const a = videoAddons.find((x) => x.id === addonId);
            const c = a?.catalogs.find((x) => x.key === catalog);
            setSource({ kind: 'addon', list: 'catalog', addonId, catalog, name: c && a ? `${c.name} · ${a.name}` : undefined });
            if (c && a) onChange({ title: c.name, source: { kind: 'addon', list: 'catalog', addonId, catalog, name: `${c.name} · ${a.name}` } });
          }}>
            {videoAddons.filter((a) => a.widgets).map((a) => (
              <optgroup key={a.id} label={a.name}>
                {a.catalogs.filter((c) => !c.searchOnly).map((c) => <option key={c.key} value={`${a.id}|${c.key}`}>{c.name}{c.type ? ` (${c.type === 'series' ? 'series' : c.type === 'movie' ? 'films' : c.type})` : ''}</option>)}
              </optgroup>
            ))}
          </select>
        </label>
      )}

      {s.kind === 'plex' && (
        <>
          <label className="ed-field">Lijst
            <select value={s.list} onChange={(e) => setSource({ ...s, list: e.target.value as typeof s.list })}>
              {PLEX_LISTS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
          </label>
          {!['continueWatching', 'watchlist', 'playlist', 'shelf'].includes(s.list) && (
            <label className="ed-field">Bibliotheek
              <select value={s.library ?? ''} onChange={(e) => setSource({ ...s, library: e.target.value || undefined, genre: undefined })}>
                {s.list === 'recentlyAdded' && <option value="">Alle bibliotheken</option>}
                {videoLibs.map((l) => <option key={l.key} value={l.key}>{l.title}</option>)}
              </select>
            </label>
          )}
          {s.list === 'genre' && (
            <label className="ed-field">Genre
              <select value={s.genre ?? ''} onChange={(e) => setSource({ ...s, genre: e.target.value || undefined })}>
                <option value="">Kies een genre…</option>
                {plexGenres.map((g) => <option key={g.key} value={g.key}>{g.title}</option>)}
              </select>
            </label>
          )}
          {s.list === 'collection' && (
            <label className="ed-field">Collectie
              <select value={s.collection ?? ''} onChange={(e) => { const c = collections.find((x) => x.ratingKey === e.target.value); setSource({ ...s, collection: e.target.value || undefined, name: c?.title }); if (c && (!w.title || w.title === 'Widget' || w.title === 'Recent toegevoegd')) onChange({ title: c.title }); }}>
                <option value="">Kies een collectie…</option>
                {collections.map((c) => <option key={c.ratingKey} value={c.ratingKey}>{c.title}{c.childCount ? ` (${c.childCount})` : ''}</option>)}
              </select>
            </label>
          )}
          {s.list === 'playlist' && (
            <label className="ed-field">Playlist
              <select value={s.playlist ?? ''} onChange={(e) => { const p = playlists.find((x) => x.ratingKey === e.target.value); setSource({ ...s, playlist: e.target.value || undefined, name: p?.title }); if (p && (!w.title || w.title === 'Widget' || w.title === 'Recent toegevoegd')) onChange({ title: p.title }); }}>
                <option value="">{playlists.length ? 'Kies een playlist…' : 'Geen video-playlists gevonden'}</option>
                {playlists.map((p) => <option key={p.ratingKey} value={p.ratingKey}>{p.title}{p.leafCount ? ` (${p.leafCount})` : ''}</option>)}
              </select>
            </label>
          )}
          {s.list === 'shelf' && (
            <label className="ed-field">Soort
              <select value={s.shelf ?? 'dv'} onChange={(e) => { const k = e.target.value as ShelfKind; setSource({ ...s, shelf: k }); if (!w.title || w.title === 'Widget' || w.title === 'Recent toegevoegd' || Object.values(SHELF_NAMES).includes(w.title)) onChange({ title: SHELF_NAMES[k] }); }}>
                {(Object.keys(SHELF_NAMES) as ShelfKind[]).map((k) => <option key={k} value={k}>{SHELF_NAMES[k]}</option>)}
              </select>
            </label>
          )}
          {s.list === 'watchlist' && <span className="subtle hint">Je watchlist van Plex (dezelfde als in de Plex-apps). Op infopagina's zet je titels erop met "Op mijn lijst".</span>}
        </>
      )}

      {s.kind === 'tmdb' && (
        <>
          <label className="ed-field">Lijst
            <select value={s.list} onChange={(e) => setSource({ ...s, list: e.target.value as typeof s.list, media: ['now_playing', 'upcoming'].includes(e.target.value) ? 'movie' : e.target.value === 'on_the_air' ? 'tv' : s.media })}>
              {TMDB_LISTS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
          </label>
          {!['now_playing', 'upcoming', 'on_the_air'].includes(s.list) && (
            <div className="ed-field">
              <span>Soort</span>
              <div className="chip-wrap">
                {(['movie', 'tv', ...(s.list.startsWith('trending') ? ['all'] : [])] as ('movie' | 'tv' | 'all')[]).map((m) => (
                  <button key={m} className="chip" aria-pressed={s.media === m} onClick={() => setSource({ ...s, media: m })}>{m === 'movie' ? 'Films' : m === 'tv' ? 'Series' : 'Beide'}</button>
                ))}
              </div>
            </div>
          )}
          {(s.list === 'provider' || s.list === 'genre') && (
            <div className="ed-field">
              <span>Volgorde</span>
              <div className="chip-wrap">
                <button className="chip" aria-pressed={s.sort !== 'new'} onClick={() => setSource({ ...s, sort: undefined })}>Populairste</button>
                <button className="chip" aria-pressed={s.sort === 'new'} onClick={() => setSource({ ...s, sort: 'new' })}>Nieuwste eerst</button>
              </div>
            </div>
          )}
          {s.list === 'provider' && (
            <label className="ed-field">Streamingdienst (NL)
              <select value={s.providerId ?? ''} onChange={(e) => setSource({ ...s, providerId: Number(e.target.value) || undefined, media: s.media === 'all' ? 'movie' : s.media })}>
                <option value="">Kies…</option>
                {options?.providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
          )}
          {s.list === 'genre' && (
            <label className="ed-field">Genre
              <select value={s.genreId ?? ''} onChange={(e) => setSource({ ...s, genreId: Number(e.target.value) || undefined, media: s.media === 'all' ? 'movie' : s.media })}>
                <option value="">Kies…</option>
                {(s.media === 'tv' ? options?.genres.tv : options?.genres.movie)?.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </label>
          )}
        </>
      )}

      {s.kind === 'mdblist' && (
        <>
          {options && options.mdblistTop.length > 0 && (
            <label className="ed-field">Populaire lijsten
              <select value={options.mdblistTop.some((l) => l.id === s.list) ? s.list : ''} onChange={(e) => {
                const l = options.mdblistTop.find((x) => x.id === e.target.value);
                if (l) { setSource({ kind: 'mdblist', list: l.id, name: l.name }); setMdbUrl(l.id); if (!w.title || w.title === 'Widget') onChange({ title: l.name }); }
              }}>
                <option value="">Kies…</option>
                {options.mdblistTop.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.items})</option>)}
              </select>
            </label>
          )}
          <label className="ed-field">Of plak een MDBList-link
            <input className="text-input" value={mdbUrl} onChange={(e) => setMdbUrl(e.target.value)} onBlur={() => setSource({ kind: 'mdblist', list: mdbUrl.trim(), name: s.name })} placeholder="https://mdblist.com/lists/gebruiker/lijstnaam" />
          </label>
          <span className="subtle hint">Bijv. Top 10 Netflix NL, IMDb Top 250, RT Certified Fresh. Zoek lijsten op mdblist.com.</span>
        </>
      )}

      {s.kind === 'trakt' && (
        <>
          <label className="ed-field">Lijst
            <select value={s.list} onChange={(e) => {
              const list = e.target.value as typeof s.list;
              setSource(list === 'custom' ? { kind: 'trakt', list: 'custom', media: 'all', url: s.url ?? '' } : { kind: 'trakt', list, media: s.media === 'tv' ? 'tv' : 'movie' });
            }}>
              {TRAKT_LISTS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
          </label>
          {s.list === 'custom' ? (
            <>
              <LinkField
                label="Link naar een Trakt-lijst"
                placeholder="https://trakt.tv/users/naam/lists/lijstnaam"
                value={s.url ?? ''}
                onCommit={(url) => setSource({ ...s, url, name: s.name ?? nameFromUrl(url) })}
              />
              <span className="subtle hint">Elke openbare lijst van trakt.tv werkt, ook officiële lijsten (trakt.tv/lists/…). Films en series door elkaar.</span>
            </>
          ) : (
            <div className="ed-field">
              <span>Soort</span>
              <div className="chip-wrap">
                {(['movie', 'tv'] as const).map((m) => <button key={m} className="chip" aria-pressed={s.media === m} onClick={() => setSource({ ...s, media: m })}>{m === 'movie' ? 'Films' : 'Series'}</button>)}
              </div>
            </div>
          )}
          {(s.list === 'watchlist' || s.list === 'recommendations') && !avail?.traktLinked && (
            <span className="error hint">Koppel eerst je Trakt-account bij <a {...linkProps('/settings/connections')}>Instellingen</a>.</span>
          )}
        </>
      )}

      {s.kind === 'simkl' && (
        <>
          <div className="ed-field">
            <span>Lijst</span>
            <div className="chip-wrap">
              {([['trending', 'Trending'], ['plantowatch', 'Plan to watch'], ['watching', 'Bezig'], ['custom', 'Eigen lijst (link)']] as const).map(([id, label]) => (
                <button key={id} className="chip" aria-pressed={s.list === id} onClick={() => setSource(id === 'custom' ? { kind: 'simkl', list: 'custom', media: 'all', url: s.url ?? '' } : { ...s, list: id, media: s.media === 'all' ? 'anime' : s.media })}>{label}</button>
              ))}
            </div>
          </div>
          {s.list === 'custom' && (
            <>
              <LinkField
                label="Link naar een SIMKL-lijst"
                placeholder="https://simkl.com/lists/12345/naam"
                value={s.url ?? ''}
                onCommit={(url) => setSource({ ...s, url, name: s.name ?? nameFromUrl(url) })}
              />
              <span className="subtle hint">Elke openbare lijst van simkl.com/lists. Let op: SIMKL geeft lijsten via hun API alleen aan PRO- of VIP-accounts, en je SIMKL-account moet gekoppeld zijn.</span>
            </>
          )}
          {s.list !== 'custom' && <div className="ed-field">
            <span>Soort</span>
            <div className="chip-wrap">
              {([['movies', 'Films'], ['tv', 'Series'], ['anime', 'Anime']] as const).map(([id, label]) => (
                <button key={id} className="chip" aria-pressed={s.media === id} onClick={() => setSource({ ...s, media: id })}>{label}</button>
              ))}
            </div>
          </div>}
          {s.list === 'trending' && (
            <div className="ed-field">
              <span>Periode</span>
              <div className="chip-wrap">
                {([['today', 'Vandaag'], ['week', 'Deze week'], ['month', 'Deze maand']] as const).map(([id, label]) => (
                  <button key={id} className="chip" aria-pressed={(s.period ?? 'week') === id} onClick={() => setSource({ ...s, period: id })}>{label}</button>
                ))}
              </div>
            </div>
          )}
          {s.list !== 'trending' && !avail?.simklLinked && (
            <span className="error hint">Koppel eerst je SIMKL-account bij <a {...linkProps('/settings/connections')}>Instellingen</a>.</span>
          )}
        </>
      )}

      {s.kind === 'personal' && (
        <>
          <div className="ed-field">
            <span>Soort</span>
            <div className="chip-wrap">
              {([['because', 'Omdat je … keek'], ['actor', 'Met een acteur'], ['director', 'Meer van de regisseur'], ['saga', 'Verder in je reeks'], ['available', 'Nu beschikbaar']] as const).map(([id, label]) => (
                <button key={id} className="chip" aria-pressed={s.list === id} onClick={() => setSource({ ...s, list: id })}>{label}</button>
              ))}
            </div>
          </div>
          <span className="subtle hint">{s.list === 'available'
            ? 'Titels van Mijn lijst die je nu kunt kijken: in de Plex-bibliotheek, of met een addon-bron die meteen speelt. Wat net beschikbaar kwam staat vooraan.'
            : 'Gebaseerd op wat dit profiel onlangs keek; de kop van de rij past zich aan ("Omdat je Reacher keek", "Met Alan Ritchson…"). Wisselt elke dag.'}</span>
        </>
      )}

      {s.kind === 'live' && (
        <>
          <div className="ed-field">
            <span>Wat</span>
            <div className="chip-wrap">
              {([['sport', 'Wedstrijden'], ['leagues', "Competitielogo's"], ['nowtv', 'Nu op tv']] as const).map(([id, label]) => (
                <button key={id} className="chip" aria-pressed={s.list === id} onClick={() => setSource({ kind: 'live', list: id })}>{label}</button>
              ))}
            </div>
          </div>
          {s.list === 'leagues' ? (
            <LeaguePicker value={s.leagues} onChange={(ids) => setSource({ ...s, leagues: ids })} />
          ) : s.list === 'nowtv' ? (
            <NowTvPicker
              channels={s.channels}
              categories={s.categories}
              onChange={(channels, categories) => setSource({ kind: 'live', list: 'nowtv', channels: channels.length ? channels : undefined, categories: categories.length ? categories : undefined })}
            />
          ) : (
            <SportFilter
              value={s}
              onChange={(p) => setSource({ ...s, ...p })}
            />
          )}
        </>
      )}

      {s.kind === 'brands' && (
        <div className="ed-field">
          <span>Wat</span>
          <div className="chip-wrap">
            {([['networks', 'Netwerken & diensten'], ['studios', "Studio's"], ['categories', 'Categorieën']] as const).map(([id, label]) => (
              <button key={id} className="chip" aria-pressed={s.list === id} onClick={() => setSource({ ...s, list: id })}>{label}</button>
            ))}
          </div>
          <span className="subtle hint">Logo's van HBO, Disney+, Netflix… of tegels als "Gebaseerd op een boek". Klikken opent een pagina met alles daarvan.</span>
        </div>
      )}

      {s.kind === 'calendar' && (
        <>
          <div className="ed-field">
            <span>Wat</span>
            <div className="chip-wrap">
              {([['episodes', 'Nieuwe afleveringen'], ['movies', 'Films die uitkomen'], ['all', 'Allebei']] as const).map(([id, label]) => (
                <button key={id} className="chip" aria-pressed={s.list === id} onClick={() => setSource({ ...s, list: id })}>{label}</button>
              ))}
            </div>
          </div>
          <span className="subtle hint">Afleveringen van series in je bibliotheek (en wat je op Trakt volgt), films die je via Seerr hebt aangevraagd of op je Trakt-watchlist staan. Vandaag en de komende dagen eerst.</span>
        </>
      )}

      {s.kind !== 'brands' && !(s.kind === 'live' && s.list === 'leagues') && (
        <div className="ed-field">
          <span>Kaartstijl</span>
          <div className="chip-wrap">
            {([['default', 'Standaard'], ['film', 'Filmstrip'], ['border', 'Rand'], ['overlay', 'Titel op beeld']] as const).map(([id, label]) => (
              <button key={id} className="chip" aria-pressed={(w.style ?? 'default') === id} onClick={() => onChange({ style: id })}>{label}</button>
            ))}
          </div>
        </div>
      )}

      {(['tmdb', 'mdblist', 'trakt', 'simkl', 'personal', 'plex', 'addon', 'merge'] as string[]).includes(s.kind) && !(s.kind === 'plex' && s.list === 'continueWatching') && (
        <RowFilterFields filter={w.filter} external={s.kind !== 'plex' && s.kind !== 'addon'} onChange={(filter) => onChange({ filter })} />
      )}

      <div className="ed-field">
        <span>Weergave</span>
        <div className="ed-views" role="radiogroup" aria-label="Weergave">
          {VIEWS.map((v) => (
            <button key={v.id} role="radio" aria-checked={w.view === v.id} className={`ed-view view-${v.id}`} onClick={() => onChange({ view: v.id, limit: v.id === 'top10' ? 10 : v.id === 'spotlight' || v.id === 'billboard' ? 6 : w.limit === 10 || w.limit === 6 ? 20 : w.limit })}>
              <span className="ed-view-pic" aria-hidden="true"><i /><i /><i /></span>
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {external && (
        <div className="ed-field">
          <span>Tonen</span>
          <div className="chip-wrap">
            {(['all', 'owned', 'missing'] as const).map((x) => (
              <button key={x} className="chip" aria-pressed={w.show === x} onClick={() => onChange({ show: x })}>
                {x === 'all' ? 'Alles' : x === 'owned' ? 'Alleen in bibliotheek' : 'Alleen aanvragen'}
              </button>
            ))}
          </div>
          <span className="subtle hint">Wat je hebt krijgt Afspelen, de rest Aanvragen via Seerr. Kinderprofielen zien alleen wat in hun bibliotheek staat.</span>
        </div>
      )}

      <label className="ed-field">Aantal
        <input className="text-input text-input-sm" type="number" min={3} max={40} value={w.limit} onChange={(e) => onChange({ limit: Math.min(40, Math.max(3, Number(e.target.value) || 20)) })} />
      </label>

      <div className="ed-field">
        <span>Voorbeeld</span>
        <div className="ed-preview">
          <WidgetView key={previewKey} w={{ ...previewW, enabled: true }} preview />
        </div>
      </div>
    </div>
  );
}

/** Tekstveld voor een lijst-link: pas opslaan bij verlaten of Enter (niet bij elke toets). */
function LinkField({ label, placeholder, value, onCommit }: { label: string; placeholder: string; value: string; onCommit: (v: string) => void }) {
  const [v, setV] = useState(value);
  return (
    <label className="ed-field">{label}
      <input
        className="text-input"
        value={v}
        placeholder={placeholder}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => v.trim() !== value && onCommit(v.trim())}
        onKeyDown={(e) => { if (e.key === 'Enter' && v.trim() !== value) onCommit(v.trim()); }}
      />
    </label>
  );
}

/** "…/lists/12345/beste-anime-2024" → "Beste anime 2024" */
function nameFromUrl(url: string): string | undefined {
  const slug = url.replace(/[?#].*$/, '').split('/').filter(Boolean).pop();
  if (!slug || /^\d+$/.test(slug)) return undefined;
  const t = decodeURIComponent(slug).replace(/[-_]+/g, ' ').trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : undefined;
}

const SC_LISTS: { id: 'ticker' | 'club' | 'table' | 'form'; label: string; title: string }[] = [
  { id: 'ticker', label: 'Live-ticker', title: 'Live uitslagen' },
  { id: 'club', label: 'Mijn club', title: 'Mijn club' },
  { id: 'table', label: 'Stand', title: 'Stand' },
  { id: 'form', label: 'Vorm', title: 'Vorm' },
];

type ScSource = Extract<WidgetSource, { kind: 'sportcenter' }>;

/** Sportwidget: ticker, één club, stand of vorm (clubs komen uit Sport → Clubs & competities). */
function SportSourceFields({ s, onChange }: { s: ScSource; onChange: (src: ScSource, title?: string) => void }) {
  const settings = useScSettings();
  const [leagues, setLeagues] = useState<ScLeague[]>([]);
  useEffect(() => {
    void loadScLeagues().then(setLeagues).catch(() => undefined);
  }, []);
  const teams = settings?.teams ?? [];
  return (
    <>
      <div className="ed-field">
        <span>Soort</span>
        <div className="chip-wrap">
          {SC_LISTS.map((l) => (
            <button key={l.id} type="button" className="chip" aria-pressed={s.list === l.id} onClick={() => onChange({ ...s, list: l.id }, l.title)}>{l.label}</button>
          ))}
        </div>
      </div>
      {s.list !== 'ticker' && (
        <label className="ed-field">Club
          <select value={s.team ?? ''} onChange={(e) => onChange({ ...s, team: e.target.value || undefined })}>
            <option value="">Mijn eerste club{teams[0] ? ` (${teams[0].short})` : ''}</option>
            {teams.map((t) => <option key={`${t.path}|${t.id}`} value={`${t.path}|${t.id}`}>{t.name}</option>)}
          </select>
        </label>
      )}
      {s.list === 'table' && (
        <label className="ed-field">Competitie
          <select value={s.league ?? ''} onChange={(e) => onChange({ ...s, league: e.target.value || undefined })}>
            <option value="">Die van de club</option>
            {leagues.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
      )}
      <span className="subtle hint">
        {s.list === 'ticker' ? 'Lopende en komende wedstrijden van vandaag in je competities, je clubs voorop. Ververst vanzelf tijdens live wedstrijden.' : 'Clubs kies je bij Sport → Clubs & competities.'}
        {!teams.length && s.list !== 'ticker' && <> Je volgt nog geen clubs: <a {...linkProps('/sportcenter/kiezen')}>kies ze hier</a>.</>}
      </span>
    </>
  );
}

/** Collecties uit Nuvio/ARVIO (JSON of link): elke collectie wordt een hub, elke map een rij. */
function ImportCollections({ room, onClose, onImport }: { room: number; onClose: () => void; onImport: (hubs: HubDef[], note: string) => void }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async () => {
    const v = text.trim();
    if (!v) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await layoutApi.importCollections(/^https:\/\//i.test(v) ? { url: v } : { json: v });
      const kept = r.hubs.slice(0, Math.max(0, room));
      if (!kept.length) throw new Error('Er is geen ruimte meer: maximaal 12 hubs. Verwijder eerst een hub.');
      const dropped = r.hubs.length - kept.length;
      const parts = [`${kept.length} ${kept.length === 1 ? 'hub' : 'hubs'} met ${kept.reduce((n, h) => n + h.widgets.length, 0)} rijen toegevoegd uit "${r.name}". Vergeet niet op te slaan.`];
      if (dropped > 0) parts.push(`${dropped} ${dropped === 1 ? 'collectie paste' : 'collecties pasten'} niet meer (maximaal 12 hubs).`);
      if (r.skipped.length) parts.push(`Overgeslagen (onbekende bron of addon die je niet hebt): ${r.skipped.slice(0, 6).join(', ')}${r.skipped.length > 6 ? ` en nog ${r.skipped.length - 6}` : ''}.`);
      onImport(kept, parts.join(' '));
    } catch (e) {
      setErr(e instanceof ApiError && e.message !== e.code ? e.message : e instanceof Error ? e.message : 'Importeren mislukt.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="ed-import" role="dialog" aria-label="Collecties importeren">
      <p>
        Plak de JSON van een Nuvio- of ARVIO-collectie, of een https-link ernaar (bijvoorbeeld uit ARVIO Collection Studio).
        Elke collectie wordt een hub en elke map een rij. Werkt met TMDB (studio's, netwerken, filmreeksen, lijsten, filters, acteurs),
        openbare Trakt- en MDBList-lijsten en catalogi van addons die je al hebt.
      </p>
      <textarea className="text-input" rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder='{"name": "…", "collections": [ … ]}  of  https://…/collections.json' spellCheck={false} />
      {err && <p className="error" role="alert">{err}</p>}
      <div className="ed-import-actions">
        <button className="btn btn-ghost" onClick={onClose}>Annuleren</button>
        <button className="btn btn-primary" disabled={busy || !text.trim()} onClick={() => void run()}>{busy ? 'Bezig…' : 'Importeren'}</button>
      </div>
    </div>
  );
}

const LANGS: [string, string][] = [['hi', 'Hindi'], ['ta', 'Tamil'], ['te', 'Telugu'], ['ko', 'Koreaans'], ['ja', 'Japans'], ['zh', 'Chinees'], ['es', 'Spaans'], ['tr', 'Turks'], ['ar', 'Arabisch'], ['ru', 'Russisch'], ['th', 'Thai'], ['tl', 'Tagalog']];

/** Volgorde en filters van een rij (zoals Xperience): schudden, minimale score, digitaal uit, talen, gezien. */
function RowFilterFields({ filter, external, onChange }: { filter: RowFilter | undefined; external: boolean; onChange: (f: RowFilter | undefined) => void }) {
  const f = filter ?? {};
  const set = (patch: Partial<RowFilter>) => {
    const next: RowFilter = { ...f, ...patch };
    for (const k of Object.keys(next) as (keyof RowFilter)[]) {
      const v = next[k];
      if (v === undefined || v === false || (Array.isArray(v) && !v.length)) delete next[k];
    }
    onChange(Object.keys(next).length ? next : undefined);
  };
  return (
    <div className="ed-field ed-filter">
      <span>Volgorde en filters</span>
      <div className="chip-wrap" role="radiogroup" aria-label="Volgorde">
        {([[undefined, 'Zoals de bron'], ['daily', 'Elke dag geschud'], ['weekly', 'Elke week geschud']] as const).map(([v, label]) => (
          <button key={label} className="chip" aria-pressed={f.shuffle === v} onClick={() => set({ shuffle: v })}>{label}</button>
        ))}
      </div>
      <div className="chip-wrap" role="radiogroup" aria-label="Minimale score">
        {([[undefined, 'Elke score'], [6.5, '★ 6.5+'], [7, '★ 7+'], [7.5, '★ 7.5+'], [8, '★ 8+']] as const).map(([v, label]) => (
          <button key={label} className="chip" aria-pressed={f.minScore === v} onClick={() => set({ minScore: v })}>{label}</button>
        ))}
      </div>
      <label className="ed-check"><input type="checkbox" checked={!!f.hideWatched} onChange={(e) => set({ hideWatched: e.target.checked })} /> Verberg wat ik al gezien heb</label>
      {external && (
        <>
          <label className="ed-check"><input type="checkbox" checked={!!f.digitalOnly} onChange={(e) => set({ digitalOnly: e.target.checked })} /> Alleen films die digitaal uit zijn (geen bioscoopfilms zonder bron)</label>
          <details className="ed-langs">
            <summary>Talen uitsluiten{f.excludeLangs?.length ? ` (${f.excludeLangs.length})` : ''}</summary>
            <div className="chip-wrap">
              {LANGS.map(([code, name]) => {
                const on = !!f.excludeLangs?.includes(code);
                return <button key={code} className="chip" aria-pressed={on} onClick={() => set({ excludeLangs: on ? f.excludeLangs!.filter((x) => x !== code) : [...(f.excludeLangs ?? []), code] })}>{name}</button>;
              })}
            </div>
            <span className="subtle hint">Op de originele taal van de titel (TMDB-rijen).</span>
          </details>
        </>
      )}
    </div>
  );
}

const MERGEABLE = ['tmdb', 'mdblist', 'trakt', 'simkl', 'personal'];

/** Samengevoegde rij (zoals Xperience): deze bron plus andere rijen van deze hub, om en om zonder dubbele titels. */
function MergeFields({ s, siblings, onChange }: { s: WidgetSource; siblings: WidgetDef[]; onChange: (s: WidgetSource) => void }) {
  const parts: WidgetSource[] = s.kind === 'merge' ? s.parts : MERGEABLE.includes(s.kind) ? [s] : [];
  if (!parts.length) return null;
  const others = siblings.filter((x) => MERGEABLE.includes(x.source.kind));
  const has = (src: WidgetSource) => parts.some((p) => JSON.stringify(p) === JSON.stringify(src));
  const toggle = (src: WidgetSource) => {
    const next = has(src) ? parts.filter((p) => JSON.stringify(p) !== JSON.stringify(src)) : [...parts, src].slice(0, 4);
    onChange(next.length >= 2 ? { kind: 'merge', list: 'merge', parts: next } : next[0]);
  };
  if (!others.length && s.kind !== 'merge') return null;
  return (
    <div className="ed-field ed-filter">
      <span>Samenvoegen met andere rijen</span>
      {s.kind === 'merge' && (
        <ul className="ed-merge">
          {parts.map((p, i) => (
            <li key={i}>
              <span>{describe(p)}</span>
              <button className="icon-mini" aria-label="Uit deze rij halen" onClick={() => onChange(parts.length > 2 ? { kind: 'merge', list: 'merge', parts: parts.filter((_, j) => j !== i) } : parts[i === 0 ? 1 : 0])}>✕</button>
            </li>
          ))}
        </ul>
      )}
      {others.map((o) => (
        <label key={o.id} className="ed-check">
          <input type="checkbox" checked={has(o.source)} disabled={!has(o.source) && parts.length >= 4} onChange={() => toggle(o.source)} />
          {o.title} <span className="subtle">· {describe(o.source)}</span>
        </label>
      ))}
      <span className="subtle hint">De titels komen om en om in één rij, zonder dubbele. Zet de losse rijen daarna uit als je ze niet twee keer wilt zien. Maximaal 4.</span>
    </div>
  );
}

/** Eigen hoes voor een hub: een achtergrond (van TMDB of een eigen link), een kleur en/of een emoji. */
function HubCoverField({ hub, onChange }: { hub: HubDef; onChange: (c: HubDef['cover']) => void }) {
  const c = hub.cover ?? {};
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [pics, setPics] = useState<{ title: string; url: string }[] | null>(null);
  const set = (patch: Partial<NonNullable<HubDef['cover']>>) => {
    const next = { ...c, ...patch };
    onChange(next.image || next.color || next.icon ? next : undefined);
  };
  const search = async () => {
    const r = await call<{ images: { title: string; url: string }[] }>(`/api/hubs/backdrops?q=${encodeURIComponent(q.trim())}`).catch(() => ({ images: [] }));
    setPics(r.images);
  };
  if (!open) {
    return <button className="btn btn-ghost btn-sm" onClick={() => { setOpen(true); if (!pics) void search(); }}>{hub.cover ? 'Hoes aanpassen' : 'Hoes kiezen'}</button>;
  }
  return (
    <div className="hub-cover-edit" role="group" aria-label="Hoes van de hub">
      <div className="ed-label">Hoes</div>
      <label className="ed-field">Emoji (optioneel)
        <input className="text-input" value={c.icon ?? ''} maxLength={4} onChange={(e) => set({ icon: e.target.value || undefined })} placeholder="🎬" />
      </label>
      <label className="ed-field">Kleur
        <input type="color" value={c.color ?? '#1f2937'} onChange={(e) => set({ color: e.target.value })} />
      </label>
      <form className="bk-form" onSubmit={(e) => { e.preventDefault(); void search(); }}>
        <input className="text-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek een film of serie voor de achtergrond" />
        <button className="btn btn-sm" type="submit">{q.trim() ? 'Zoeken' : 'Trending'}</button>
      </form>
      {pics && (
        <div className="hub-cover-pics">
          {pics.map((p) => (
            <button key={p.url} type="button" aria-pressed={c.image === p.url} title={p.title} onClick={() => set({ image: c.image === p.url ? undefined : p.url })}>
              <img src={p.url} alt={p.title} loading="lazy" />
            </button>
          ))}
        </div>
      )}
      <div className="form-actions">
        {hub.cover && <button className="btn btn-ghost btn-sm" onClick={() => onChange(undefined)}>Hoes weghalen</button>}
        <button className="btn btn-sm" onClick={() => setOpen(false)}>Klaar</button>
      </div>
      <span className="subtle hint">Heeft een hub een achtergrond of kleur, dan zie je de hubs als kaarten. Vergeet niet op te slaan.</span>
    </div>
  );
}
